import React, { FC, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

import { Timeline } from '../ui/timeline';
import { MovingBorder } from '../ui/moving-border';
import { Reveal } from '../ui/reveal';
import { Button } from '../ui/button';
import { WorkExperienceItem } from './types/work-experience';
import { WorkExperienceCard } from './work-experience-card';

interface WorkExperienceProps {
  data: WorkExperienceItem[];
}

export const WorkExperience: FC<WorkExperienceProps> = ({ data }) => {
  const sectionRef = useRef<HTMLElement | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const initialCount = 3;
  const hasMore = data.length > initialCount;
  const visibleData = isExpanded ? data : data.slice(0, initialCount);

  const items = useMemo(() => visibleData.map((item) => ({
    title: item.date,
    content: <WorkExperienceCard key={item.date} item={item} />,
  })), [visibleData]);

  return (
    <section id="work-experience" ref={sectionRef} className="scroll-mt-28">
      <Reveal>
        <h1 className="heading mb-20">
          My
          {' '}
          <span className="text-purple">work experience.</span>
        </h1>
      </Reveal>
      <div className="flex flex-col gap-y-10">
        <div id="work-experience-timeline">
          <MovingBorder duration={8000} rx="0%" ry="0%" borderClassName="w-48 h-48">
            <Timeline data={items} />
          </MovingBorder>
        </div>

        {hasMore && (
          <div className="flex justify-center">
            <Button
              variant="magic"
              aria-expanded={isExpanded}
              aria-controls="work-experience-timeline"
              onClick={() => {
                setIsExpanded((prev) => {
                  const next = !prev;
                  if (prev && !next) {
                    sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                  return next;
                });
              }}
            >
              {isExpanded ? 'Show less' : 'Show more'}
              {isExpanded ? <ChevronUp className="size-4 ml-2" /> : <ChevronDown className="size-4 ml-2" />}
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};
